import type { ComponentChildren } from "preact";
import { ApiError } from "../api.js";
import type { Locale } from "../types.js";
import {
  uiMessage,
  type Kit321MessageKey,
} from "../../../../../src/i18n/runtime.js";
import type { ProductMessageValues } from "@xunbei/i18n";

export function productCopy(locale: Locale, en: string, zh: string): string {
  return locale === "zh-CN" ? zh : en;
}

export function tx(
  locale: Locale,
  key: Kit321MessageKey,
  values?: ProductMessageValues,
): string;
export function tx(locale: Locale, english: string, chinese: string): string;
export function tx(
  locale: Locale,
  keyOrEnglish: string,
  valuesOrChinese?: ProductMessageValues | string,
): string {
  if (keyOrEnglish.startsWith("ui.")) {
    return uiMessage(
      keyOrEnglish as Kit321MessageKey,
      locale,
      typeof valuesOrChinese === "string" ? undefined : valuesOrChinese,
    );
  }
  if (typeof valuesOrChinese === "string")
    return productCopy(locale, keyOrEnglish, valuesOrChinese);
  throw new Error(`Unknown 321Kit UI message key: ${keyOrEnglish}`);
}

export function Eyebrow({ children }: { children: ComponentChildren }) {
  return <div className="eyebrow">{children}</div>;
}

export function PageHeader({
  eyebrow,
  title,
  detail,
  action,
}: {
  eyebrow: ComponentChildren;
  title: string;
  detail: string;
  action?: ComponentChildren;
}) {
  return (
    <header className="page-header">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1>{title}</h1>
        <p>{detail}</p>
      </div>
      {action && <div className="page-actions">{action}</div>}
    </header>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ComponentChildren;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function InlineError({ children }: { children: ComponentChildren }) {
  return (
    <div className="inline-error" role="alert">
      {children}
    </div>
  );
}

export function statusLabel(value: string, locale: Locale): string {
  const labels: Record<string, [string, string]> = {
    active: ["Active", "使用中"],
    disabled: ["Disabled", "已停用"],
    draft: ["Draft", "草稿"],
    published: ["Published", "已发布"],
    accepted: ["Submitted", "已提交"],
    flagged: ["Flagged", "待处理"],
    deleted: ["Deleted", "已删除"],
  };
  const label = labels[value];
  return label ? tx(locale, ...label) : value;
}

export function Status({ value, locale }: { value: string; locale: Locale }) {
  return (
    <span className={`status ${value}`}>{statusLabel(value, locale)}</span>
  );
}

export function Skeleton() {
  return (
    <div className="skeleton">
      <i />
      <i />
      <i />
    </div>
  );
}

export function Kpi({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="kpi">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export function AppLink({
  href,
  className,
  children,
}: {
  href: string;
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  );
}

export function message(reason: unknown): string {
  return reason instanceof ApiError || reason instanceof Error
    ? reason.message
    : "The request failed.";
}

export function formatDate(value: string, locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "zh-CN" ? "zh-CN" : "en", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

export function formatBytes(value: number): string {
  return value < 1024
    ? `${value} B`
    : value < 1024 * 1024
      ? `${(value / 1024).toFixed(1)} KiB`
      : `${(value / 1024 / 1024).toFixed(1)} MiB`;
}
